<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell [class.setup-dialog-shell]="isDialogMode">
  <app-page-hero *ngIf="!isDialogMode" eyebrow="Container Tracking Setup" [title]="title" [subtitle]="subtitle">
    <div actions>
      <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="cancel()"></p-button>
      <p-button label="Save" icon="pi pi-check" (click)="save()" [loading]="isSubmitting" [disabled]="form.invalid"></p-button>
    </div>
  </app-page-hero>

  <form [formGroup]="form" class="setup-form-shell">
    <app-page-data-panel [title]="title" subtitle="Complete the required values and operational references.">
      <div class="setup-section" *ngIf="master === 'facilities'">
        <div class="setup-section-header">
          <i class="pi pi-warehouse"></i>
          <div>
            <strong>Facility Identity</strong>
            <span>Core terminal, depot, CFS, bonded yard, or carrier yard identity.</span>
          </div>
        </div>
        <div class="setup-section-grid three">
          <p-inputGroup class="wide">
            <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="facilityType" formControlName="facilityType" [options]="facilityTypes" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
              <label for="facilityType">Facility Type *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="facilityCode" pInputText formControlName="facilityCode">
              <label for="facilityCode">Facility Code *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup class="span-2">
            <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="facilityName" pInputText formControlName="facilityName">
              <label for="facilityName">Facility Name *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-clock"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="operatingHours" pInputText formControlName="operatingHours">
              <label for="operatingHours">Operating Hours</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup class="span-2">
            <p-inputGroupAddon><i class="pi pi-sliders-h"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="operationMode" formControlName="operationMode" [options]="operationModeOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
              <label for="operationMode">Operation Mode *</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
      </div>

      <div class="setup-section" *ngIf="master === 'facilities'">
        <div class="setup-section-header">
          <i class="pi pi-link"></i>
          <div>
            <strong>Operational Links</strong>
            <span>Connect the facility to known locations, CRM suppliers, and warehouse setup records.</span>
          </div>
        </div>
        <div class="setup-section-grid three">
          <div class="setup-lookup-field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="locationId" formControlName="locationId" appLazyLocationPicker optionLabel="name" optionValue="id" [filter]="true" filterBy="name,longDescription,shortDescription,externalLocationReference" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="locationId">Location</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="openLocationQuickCreate()" pTooltip="Create location"></p-button>
          </div>
          <div class="setup-lookup-field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="operatorCrmClientId" formControlName="operatorCrmClientId" [options]="supplierClients" optionLabel="companyName" optionValue="id" [filter]="true" filterBy="companyName,clientNumber,primaryEmail,phoneNumber" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="operatorCrmClientId">CRM Supplier / Operator (Optional)</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="openSupplierQuickCreate()" pTooltip="Create CRM supplier"></p-button>
          </div>
          <div class="setup-reference-note span-3" *ngIf="form.get('operationMode')?.value === 'REFERENCE_ONLY'">
            <i class="pi pi-info-circle"></i>
            <span>This terminal is externally managed. A CRM supplier/operator may be linked for reference, but is not required. Yard slots, gate actions, EIR, and charge control stay outside CargoMate.</span>
          </div>
          <div class="setup-lookup-field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="linkedWarehouseId" formControlName="linkedWarehouseId" [options]="linkedWarehouseOptions" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code,locationName,parentName" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="linkedWarehouseId">Linked Warehouse</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="openWarehouseQuickCreate()" pTooltip="Create warehouse"></p-button>
          </div>
        </div>
      </div>

      <div class="setup-section" *ngIf="master === 'areas'">
        <div class="setup-section-header">
          <i class="pi pi-map"></i>
          <div>
            <strong>Area Definition</strong>
            <span>Define a yard zone and attach it to its operating facility.</span>
          </div>
        </div>
        <div class="setup-section-grid two">
          <div class="setup-lookup-field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="areaFacilityId" formControlName="facilityId" [options]="facilities" optionLabel="facilityName" optionValue="id" [filter]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="areaFacilityId">Facility *</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="openFacilityQuickCreate()" pTooltip="Create facility"></p-button>
          </div>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-tags"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="areaType" formControlName="areaType" [options]="areaTypes" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
              <label for="areaType">Area Type *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="areaCode" pInputText formControlName="areaCode">
              <label for="areaCode">Area Code *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="areaName" pInputText formControlName="areaName">
              <label for="areaName">Area Name *</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
      </div>

      <div class="setup-section" *ngIf="master === 'slots'">
        <div class="setup-section-header">
          <i class="pi pi-table"></i>
          <div>
            <strong>Slot Location</strong>
            <span>Define the facility, area, and physical slot address.</span>
          </div>
        </div>
        <div class="setup-section-grid four">
          <div class="setup-lookup-field span-2">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="slotFacilityId" formControlName="facilityId" [options]="facilities" optionLabel="facilityName" optionValue="id" [filter]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="slotFacilityId">Facility *</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="openFacilityQuickCreate()" pTooltip="Create facility"></p-button>
          </div>
          <div class="setup-lookup-field span-2">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="slotAreaId" formControlName="areaId" [options]="areaOptions" optionLabel="areaName" optionValue="id" [filter]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="slotAreaId">Area *</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="openAreaQuickCreate()" pTooltip="Create area"></p-button>
          </div>
          <p-inputGroup class="span-2">
            <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="yardProduct" formControlName="productId" [options]="logisticsProducts"
                optionLabel="name" optionValue="productID" [filter]="true" filterBy="name,productCode"
                [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
              <label for="yardProduct">Catalog Service</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup class="span-2">
            <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="yardVariant" formControlName="variantId" [options]="productVariants()"
                optionLabel="name" optionValue="variantID" [showClear]="true" appendTo="body"
                styleClass="w-full"></p-select>
              <label for="yardVariant">Catalog Variant</label>
            </p-iftaLabel>
          </p-inputGroup>
          <div class="span-4 surface-50 border-1 surface-border border-round p-3 text-sm" *ngIf="selectedProduct() as product">
            <strong>{{ product.productCode }}</strong>
            <span class="mx-2">|</span>
            <span>{{ product.currencyCode }}</span>
            <span class="mx-2">|</span>
            <span>Tax {{ product.taxRate || 0 }}%</span>
            <span class="mx-2">|</span>
            <span>Income {{ product.incomeAccountName || 'company default' }}</span>
            <span class="mx-2">|</span>
            <span>Expense {{ product.expenseAccountName || 'company default' }}</span>
          </div>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="slotCode" pInputText formControlName="slotCode">
              <label for="slotCode">Slot Code *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-th-large"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="blockCode" pInputText formControlName="blockCode">
              <label for="blockCode">Block</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-bars"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="rowCode" pInputText formControlName="rowCode">
              <label for="rowCode">Row</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-stop"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="bayCode" pInputText formControlName="bayCode">
              <label for="bayCode">Bay</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-sort-amount-up"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="tierCode" pInputText formControlName="tierCode">
              <label for="tierCode">Tier</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-inputNumber inputId="maxTeuCapacity" formControlName="maxTeuCapacity" [min]="1" [useGrouping]="false" styleClass="w-full"></p-inputNumber>
              <label for="maxTeuCapacity">Max TEU *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup class="span-2">
            <p-inputGroupAddon><i class="pi pi-filter"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="allowedContainerTypes" pInputText formControlName="allowedContainerTypes">
              <label for="allowedContainerTypes">Allowed Container Types</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
      </div>

      <div class="setup-section" *ngIf="master === 'charge-rules'">
        <div class="setup-section-header">
          <i class="pi pi-calculator"></i>
          <div>
            <strong>Charge Rule</strong>
            <span>Configure estimated CY charges by facility, area, basis, and rate.</span>
          </div>
        </div>
        <div class="setup-section-grid four">
          <div class="setup-lookup-field span-2">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="chargeFacility" formControlName="facilityId" [options]="facilities" optionLabel="facilityName" optionValue="id" [filter]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="chargeFacility">Facility *</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="openFacilityQuickCreate()" pTooltip="Create facility"></p-button>
          </div>
          <div class="setup-lookup-field span-2">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="chargeArea" formControlName="areaId" [options]="areaOptions" optionLabel="areaName" optionValue="id" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="chargeArea">Area</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="openAreaQuickCreate()" pTooltip="Create area"></p-button>
          </div>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-tags"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="chargeType" formControlName="chargeType" [options]="chargeTypes" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
              <label for="chargeType">Charge Type *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-list"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="chargeBasis" formControlName="chargeBasis" [options]="chargeBasisOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
              <label for="chargeBasis">Basis *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="containerType" pInputText formControlName="containerType">
              <label for="containerType">Container Type</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-inputNumber inputId="freeDays" formControlName="freeDays" [min]="0" [useGrouping]="false" styleClass="w-full"></p-inputNumber>
              <label for="freeDays">Free Days</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="currencyCode" pInputText formControlName="currencyCode">
              <label for="currencyCode">Currency *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-wallet"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-inputNumber inputId="rateAmount" formControlName="rateAmount" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="4" styleClass="w-full"></p-inputNumber>
              <label for="rateAmount">Rate *</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
      </div>

      <div class="setup-section compact" *ngIf="master === 'areas'">
        <div class="setup-section-header">
          <i class="pi pi-shield"></i>
          <div>
            <strong>Area Capabilities</strong>
            <span>Capabilities control future slot assignment and operational filtering.</span>
          </div>
        </div>
        <div class="toggle-grid">
          <label class="toggle-row"><p-toggleswitch formControlName="temperatureControlled"></p-toggleswitch><span>Temperature controlled</span></label>
          <label class="toggle-row"><p-toggleswitch formControlName="reeferSupported"></p-toggleswitch><span>Reefer supported</span></label>
          <label class="toggle-row"><p-toggleswitch formControlName="hazardousAllowed"></p-toggleswitch><span>Hazardous allowed</span></label>
          <label class="toggle-row"><p-toggleswitch formControlName="customsControlled"></p-toggleswitch><span>Customs controlled</span></label>
        </div>
      </div>

      <div class="setup-section compact">
        <div class="toggle-grid single">
          <label class="toggle-row">
            <p-toggleswitch formControlName="active"></p-toggleswitch>
            <span>{{ form.get('active')?.value ? 'Active' : 'Inactive' }}</span>
          </label>
        </div>
      </div>
    </app-page-data-panel>
  </form>
  <div class="setup-dialog-actions" *ngIf="isDialogMode">
    <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="cancel()"></p-button>
    <p-button [label]="'Create ' + (master === 'areas' ? 'Area' : 'Facility')" icon="pi pi-check" (click)="save()" [loading]="isSubmitting" [disabled]="form.invalid"></p-button>
  </div>
</app-page-shell>

<app-party-quick-create-dialog
  [(visible)]="locationQuickCreateVisible"
  type="location"
  [countries]="countryOptions()"
  [owningCompany]="currentCompany()"
  [defaultCountryId]="defaultCountryId()"
  titleOverride="Create Terminal Location"
  submitLabel="Create Location"
  (created)="onLocationQuickCreated($event)"
  (failed)="quickCreateFailed($event)">
</app-party-quick-create-dialog>

<p-dialog
  [(visible)]="supplierQuickCreateVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '760px', maxWidth: '96vw' }"
  styleClass="yard-supplier-quick-dialog">
  <ng-template pTemplate="header">
    <div class="quick-dialog-header">
      <span class="quick-dialog-icon"><i class="pi pi-briefcase"></i></span>
      <div>
        <strong>Create CRM Supplier</strong>
        <span>Add an optional terminal operator and select it on this facility.</span>
      </div>
    </div>
  </ng-template>

  <div class="quick-dialog-layout">
    <aside class="quick-dialog-summary">
      <span class="summary-eyebrow">Terminal operator</span>
      <strong>{{ supplierQuickCreateForm.get('companyName')?.value || 'New supplier' }}</strong>
      <p>The record is created with the Supplier business role and remains available across CRM-backed operator selectors.</p>
      <div class="summary-fact"><span>Role</span><b>Supplier</b></div>
      <div class="summary-fact"><span>Selection</span><b>Automatic after save</b></div>
    </aside>

    <form [formGroup]="supplierQuickCreateForm" class="quick-dialog-form">
      <div class="quick-form-section">
        <div class="quick-form-section-title"><i class="pi pi-building"></i><span>Supplier identity</span></div>
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="quickSupplierName" pInputText formControlName="companyName">
            <label for="quickSupplierName">Supplier Name *</label>
          </p-iftaLabel>
        </p-inputGroup>
        <small class="p-error" *ngIf="supplierQuickCreateForm.get('companyName')?.invalid && supplierQuickCreateForm.get('companyName')?.touched">Supplier name is required.</small>
      </div>
      <div class="quick-form-section">
        <div class="quick-form-section-title"><i class="pi pi-address-book"></i><span>Contact and classification</span></div>
        <div class="quick-form-grid">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-envelope"></i></p-inputGroupAddon>
            <p-iftaLabel><input id="quickSupplierEmail" pInputText formControlName="primaryEmail"><label for="quickSupplierEmail">Primary Email</label></p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-phone"></i></p-inputGroupAddon>
            <p-iftaLabel><input id="quickSupplierPhone" pInputText formControlName="phoneNumber"><label for="quickSupplierPhone">Phone Number</label></p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup class="span-2">
            <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
            <p-iftaLabel><input id="quickSupplierNature" pInputText formControlName="natureOfBusiness"><label for="quickSupplierNature">Nature of Business</label></p-iftaLabel>
          </p-inputGroup>
        </div>
      </div>
    </form>
  </div>

  <ng-template pTemplate="footer">
    <div class="quick-dialog-footer">
      <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="supplierQuickCreateVisible = false"></p-button>
      <p-button label="Create & Select" icon="pi pi-check" (click)="saveSupplierQuickCreate()" [loading]="supplierQuickCreateSaving" [disabled]="supplierQuickCreateForm.invalid"></p-button>
    </div>
  </ng-template>
</p-dialog>
